import { cn } from '@lfg/ui/lib';
import React, { HTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';

interface NodeCardProps {
  children: ReactNode;
  className?: string;
  hoverEffect?: 'shadow' | 'bgcolor';
  onClick?: HTMLAttributes<HTMLDivElement>['onClick'];
}

export const NodeCard: React.FC<NodeCardProps> = ({
  children,
  className = '',
  hoverEffect = 'bgcolor',
  onClick = () => {},
}) => {
  const maskId = useId();
  const gradient1Id = useId();
  const gradient2Id = useId();

  return (
    <div
      className={cn(
        'relative overflow-hidden transition-all duration-300 cursor-pointer',
        hoverEffect === 'shadow' && 'hover:shadow-lg hover:shadow-cyan-500/20',
        hoverEffect === 'bgcolor' && 'group',
        className,
      )}
      onClick={onClick}>
      {/* SVG background */}
      <svg
        className={cn(
          'absolute inset-0 w-full h-full transition-all duration-300',
          hoverEffect === 'bgcolor' && 'group-hover:opacity-90',
        )}
        viewBox='0 0 405 494'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        preserveAspectRatio='none'>
        <mask id={maskId} fill='white'>
          <path fillRule='evenodd' clipRule='evenodd' d='M32.0227 0H405V463.999L374.978 494H0V32L32.0227 0Z' />
        </mask>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M32.0227 0H405V463.999L374.978 494H0V32L32.0227 0Z'
          fill='#050B15'
          fillOpacity='0.95'
        />
        <path
          d='M405 0H407V-2H405V0ZM32.0227 0V-2H31.1947L30.609 -1.41471L32.0227 0ZM405 463.999L406.414 465.414L407 464.828V463.999H405ZM374.978 494V496H375.806L376.392 495.415L374.978 494ZM0 494H-2V496H0V494ZM0 32L-1.41371 30.5853L-2 31.1712V32H0ZM405 -2H32.0227V2H405V-2ZM407 463.999V0H403V463.999H407ZM376.392 495.415L406.414 465.414L403.586 462.585L373.564 492.585L376.392 495.415ZM0 496H374.978V492H0V496ZM-2 32V494H2V32H-2ZM1.41371 33.4147L33.4364 1.41471L30.609 -1.41471L-1.41371 30.5853L1.41371 33.4147Z'
          fill={`url(#${gradient1Id})`}
          mask={`url(#${maskId})`}
        />
        <path
          d='M405 0H407V-2H405V0ZM32.0227 0V-2H31.1947L30.609 -1.41471L32.0227 0ZM405 463.999L406.414 465.414L407 464.828V463.999H405ZM374.978 494V496H375.806L376.392 495.415L374.978 494ZM0 494H-2V496H0V494ZM0 32L-1.41371 30.5853L-2 31.1712V32H0ZM405 -2H32.0227V2H405V-2ZM407 463.999V0H403V463.999H407ZM376.392 495.415L406.414 465.414L403.586 462.585L373.564 492.585L376.392 495.415ZM0 496H374.978V492H0V496ZM-2 32V494H2V32H-2ZM1.41371 33.4147L33.4364 1.41471L30.609 -1.41471L-1.41371 30.5853L1.41371 33.4147Z'
          fill={`url(#${gradient2Id})`}
          mask={`url(#${maskId})`}
        />
        <defs>
          <linearGradient id={gradient1Id} x1='0' y1='0' x2='406.904' y2='386.998' gradientUnits='userSpaceOnUse'>
            <stop stopColor='white' />
            <stop offset='0.43531' stopColor='white' stopOpacity='0' />
          </linearGradient>
          <linearGradient id={gradient2Id} x1='0' y1='0' x2='407.379' y2='492.03' gradientUnits='userSpaceOnUse'>
            <stop offset='0.678408' stopColor='white' stopOpacity='0' />
            <stop offset='1' stopColor='white' />
          </linearGradient>
        </defs>
      </svg>

      {/* Hover background color overlay (only visible when hoverEffect is 'bgcolor') */}
      {hoverEffect === 'bgcolor' && (
        <div className='absolute inset-0 bg-cyan-950/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0'></div>
      )}

      {/* Content container */}
      <div className='relative overflow-hidden z-10 w-full h-full flex flex-col'>{children}</div>
    </div>
  );
};
